import { AlertCircle, Play } from 'lucide-react';

import { usePipeline } from '../../hooks/usePipeline';
import { validateMetadata } from '../../stores/metadata-validation';
import { useAppStore } from '../../stores/useAppStore';
import { Button } from '../ui/button';
import { DropZone } from './DropZone';
import { MetadataForm } from './MetadataForm';

export function IdleView(): JSX.Element {
  const files = useAppStore((s) => s.files);
  const heroIndex = useAppStore((s) => s.heroIndex);
  const metadata = useAppStore((s) => s.metadata);
  const phase = useAppStore((s) => s.phase);
  const errors = useAppStore((s) => s.errors);
  const addFiles = useAppStore((s) => s.addFiles);
  const removeFileAt = useAppStore((s) => s.removeFileAt);
  const clearFiles = useAppStore((s) => s.clearFiles);
  const setHeroIndex = useAppStore((s) => s.setHeroIndex);
  const setMetadata = useAppStore((s) => s.setMetadata);

  const { start, canStart } = usePipeline();
  const fieldErrors = validateMetadata(metadata).errors;
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;
  const disabled = phase.kind === 'validating';

  return (
    <>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        <section className="space-y-4">
          <SectionLabel
            title="写真"
            hint={`${files.length}/80 枚 — 1 枚目がヒーロー写真 (PNG)、残りは JPEG 出力`}
          />
          <DropZone
            files={files}
            heroIndex={heroIndex}
            onFilesAdded={(fs) => addFiles(fs)}
            onHeroChange={setHeroIndex}
            onFileRemoved={removeFileAt}
            onClearAll={clearFiles}
            disabled={disabled}
          />
        </section>

        <aside className="space-y-4">
          <SectionLabel title="車両情報" hint="キャプションに自動挿入されます" />
          <MetadataForm
            value={metadata}
            onChange={setMetadata}
            errors={fieldErrors}
            disabled={disabled}
          />
        </aside>
      </div>

      <ErrorBanner phase={phase} validationErrors={errors} />

      <div className="mt-8 flex items-center justify-between">
        <div className="text-text-tertiary text-xs">処理開始前にすべての項目を入力してください</div>
        <Button onClick={() => void start()} disabled={!canStart || hasFieldErrors} size="md">
          <Play className="mr-2 h-4 w-4" strokeWidth={2} aria-hidden />
          処理開始
        </Button>
      </div>
    </>
  );
}

function SectionLabel(props: { readonly title: string; readonly hint?: string }): JSX.Element {
  return (
    <div className="border-silver-tertiary flex items-end justify-between border-b pb-2">
      <h2 className="text-text-primary text-sm font-semibold tracking-tight">{props.title}</h2>
      {props.hint ? <span className="text-text-tertiary text-[11px]">{props.hint}</span> : null}
    </div>
  );
}

function ErrorBanner(props: {
  readonly phase: ReturnType<typeof useAppStore.getState>['phase'];
  readonly validationErrors: readonly string[];
}): JSX.Element | null {
  if (props.phase.kind === 'error') {
    return (
      <div className="border-status-error text-status-error mt-6 flex items-start gap-2 rounded-md border bg-[rgba(255,59,48,0.04)] p-4 text-sm">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden />
        <div>
          <div className="font-medium">処理に失敗しました</div>
          <div className="text-text-secondary text-xs">{props.phase.message}</div>
        </div>
      </div>
    );
  }
  if (props.validationErrors.length > 0) {
    return (
      <div className="border-status-warning text-status-warning mt-6 flex items-start gap-2 rounded-md border bg-[rgba(255,149,0,0.05)] p-4 text-sm">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden />
        <ul className="space-y-0.5 text-xs">
          {props.validationErrors.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      </div>
    );
  }
  return null;
}
